/**
 * The line drawings options use (options.ts `icon`), by name: one stroke path each on a 24-unit grid,
 * drawn the same way everywhere (an icon beside a name, a badge on a user icon, a pin on the map
 * canvas: map/pins.ts). The app's own interface icons (back, close, chevron) stay in @feral/ui.
 *
 * To add one: a new name and its path here, then name it as an option's `icon`.
 */
export const ICONS = {
    // ── places (spot types) ──
    /** a tree */
    tree: 'M12 3l5 7h-3l4 6H6l4-6H7z M12 16v5',
    /** a winding path */
    path: 'M5 21c3-2 6-3 5-6s-3-4 0-7 6-3 8-5 M15 21h.01 M18 17h.01',
    /** an umbrella in the sand */
    umbrella: 'M3 11a9 7 0 0 1 18 0z M12 11v9 M7 21h10',
    /** a toilet */
    toilet: 'M7 3h5v7H7z M4 10h15a4 4 0 0 1-4 5H9a4 4 0 0 1-5-5z M9 15l-1 6h7l-1-6',
    /** a dumbbell */
    dumbbell: 'M6 7v10 M18 7v10 M3 10v4 M21 10v4 M6 12h12',
    /** two lockers, vents and handles */
    lockers: 'M4 3h7v18H4z M13 3h7v18h-7z M6 6h3 M15 6h3 M6 8h3 M15 8h3 M9 13v2 M15 13v2',
    /** steam over a bench */
    steam: 'M8 3c-2 2 2 4 0 6 M12 3c-2 2 2 4 0 6 M16 3c-2 2 2 4 0 6 M4 14h16 M6 14v6 M18 14v6',
    /** a cocktail glass */
    cocktail: 'M5 4h14l-7 8z M12 12v8 M8 20h8',
    /** a shopping bag */
    bag: 'M5 8h14l-1 13H6z M9 8V6a3 3 0 0 1 6 0v2',
    /** a screen with a play mark: video booths */
    screen: 'M3 5h18v12H3z M10 8.5l5 2.5-5 2.5z M8 21h8',
    /** a truck */
    truck: 'M2 6h11v10H2z M13 9h4l4 4v3h-8 M8 18a2 2 0 1 1-4 0a2 2 0 1 1 4 0 M19 18a2 2 0 1 1-4 0a2 2 0 1 1 4 0',
    /** a parking sign: P in a square */
    parking: 'M4 3h16v18H4z M10 17V7h3.5a3 3 0 0 1 0 6H10',
    /** an eye: somewhere you go to look */
    eye: 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z M15 12a3 3 0 1 1-6 0a3 3 0 1 1 6 0',
    /** a tent */
    tent: 'M2 20h20 M12 4L3 20 M12 4l9 16 M12 14l-3 6 M12 14l3 6',
    /** a bed */
    bed: 'M3 19V6 M3 14h18v5 M21 14v-2a3 3 0 0 0-3-3h-7v5 M8.5 11a1.5 1.5 0 1 1-3 0a1.5 1.5 0 1 1 3 0',
    /** a map marker */
    marker: 'M12 21s-6-6-6-11a6 6 0 0 1 12 0c0 5-6 11-6 11z M12 10h.01',
    // ── positions: arrowheads carry the meaning (up top, down bottom, both vers, the larger head the
    // side a vers leans to) ──
    'arrow-up': 'M12 20V4 M6 10l6-6 6 6',
    'arrows-up-lean': 'M12 4v16 M4.5 11.5 12 4l7.5 7.5 M9.5 17.5 12 20l2.5-2.5',
    'arrows-up-down': 'M12 4v16 M7 9l5-5 5 5 M7 15l5 5 5-5',
    'arrows-down-lean': 'M12 4v16 M9.5 6.5 12 4l2.5 2.5 M4.5 12.5 12 20l7.5-7.5',
    'arrow-down': 'M12 4v16 M6 14l6 6 6-6',
    'arrows-side': 'M4 12h16 M8 8l-4 4 4 4 M16 8l4 4-4 4',
    // Sucking, and a Side's badge: left receives, right gives, both ways both (arrows-side).
    'arrow-left': 'M20 12H4 M10 6l-6 6 6 6',
    'arrow-right': 'M4 12h16 M14 6l6 6-6 6',
    // ── status and role badges on a user icon (docs/USER-ICONS.md IC-04, IC-07) ──
    /** a house: hosting, the door's open */
    house: 'M3 11l9-7 9 7 M5 9.5V20h14V9.5 M10 20v-6h4v6',
    /** a plane: traveling */
    plane: 'M21 3L3 10.5l7 2.5 2.5 7z M10 13l4.5-4.5',
    /** a crown: the owner */
    crown: 'M3 18h18 M3 18L4 7l5 5 3-7 3 7 5-5 1 11',
    /** a shield: a moderator */
    shield: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
    /** a hard hat: a builder, who met this month's goal (decision 64) */
    'hard-hat': 'M3 18h18 M4 18v-2a8 8 0 0 1 16 0v2 M10 8.5V6h4v2.5 M12 6v5',
    /** a gate, a rail for each Gatekeeper level (FR-VET-07): Gatekeeper, Sentinel, Guardian */
    'gate-1': 'M5 3v18 M19 3v18 M5 12h14',
    'gate-2': 'M5 3v18 M19 3v18 M5 9h14 M5 15h14',
    'gate-3': 'M5 3v18 M19 3v18 M5 7h14 M5 12h14 M5 17h14',
    /** rings, one for each Circle level (decision 65): Connected, Well Connected, Legend */
    'circle-1': 'M12 9a3 3 0 1 1 0 6a3 3 0 1 1 0-6',
    'circle-2': 'M12 9a3 3 0 1 1 0 6a3 3 0 1 1 0-6 M12 4a8 8 0 1 1 0 16a8 8 0 1 1 0-16',
    'circle-3': 'M12 10a2 2 0 1 1 0 4a2 2 0 1 1 0-4 M12 6a6 6 0 1 1 0 12a6 6 0 1 1 0-12 M12 2a10 10 0 1 1 0 20a10 10 0 1 1 0-20',
    /** a pole with a flag for each place level (decision 67): Scout, Tracker, Trailblazer */
    'flag-1': 'M6 21V3 M6 4l12 4-12 4',
    'flag-2': 'M6 21V3 M6 3l11 3.5L6 10 M6 11l11 3.5L6 18',
    'flag-3': 'M6 21V3 M6 3l11 2.5L6 8 M6 9l11 2.5L6 14 M6 15l11 2.5L6 20',
    /** a star: a founding member */
    star: 'M12 3l2.7 5.6 6.1.8-4.5 4.2 1.1 6.1L12 16.8l-5.4 2.9 1.1-6.1-4.5-4.2 6.1-.8z',
    // ── the map's other marks ──
    /** a calendar: an event (FR-EVT-02), as the app's own calendar icon draws it */
    calendar: 'M4 6h16v15H4z M4 10h16 M8 3v4 M16 3v4',
    /** two people, one behind: a group (FR-GRP-01) */
    people: 'M9 11a3 3 0 1 0 0-6a3 3 0 0 0 0 6z M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6 M16 11a2.5 2.5 0 1 0 0-5 M21 20c0-2.6-1.6-4.8-4-5.6',
    // ── an event's or a group's state, top left of its mark as a person's status is (IC-04) ──
    /** a bolt: an event on now, as the app's Status button draws it */
    bolt: 'M13 2 4 14h7l-1 8 9-12h-7l1-8z',
    /** a cross: an event called off */
    close: 'M6 6l12 12M18 6 6 18',
    /** a lock: a group closed */
    lock: 'M5 11h14v10H5zM8 11V7a4 4 0 0 1 8 0v4',
};
//# sourceMappingURL=icons.js.map